import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid2,
  IconButton,
  MenuItem,
  TextField,
  Typography
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { DeleteIcon } from 'asterX-UI/Icons/DeleteIcon'
import { PlusBlueIcon } from 'asterX-UI/Icons/PlusBlueIcon'
import MaskedTextInput from 'asterX-UI/Inputs/MackedTextInput'
import ActionModal from 'asterX-UI/Modals/ActionModal'
import { CircularProgress } from 'asterX-UI/Loaders'
import { SelectPlaceholder } from 'asterX-UI/Inputs'
import { GRAY_COLORS } from 'asterX-UI'
import { File04 } from 'asterX-UI/Icons/File04'

import { debounce } from 'lodash'
import { enqueueSnackbar } from 'notistack'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { CreditFlow } from 'lib/api/services/creditFlow.service'
import { HEAD_UNDERWRITER, SUPER_UNDERWRITER, UNDERWRITER } from 'lib/constants/roles'
import { useAuthStore } from 'context/AuthStore'
import { useContractStore } from 'context/ContractStore'
import { MODAL_CONFIG } from '../role-modals-constant'
import { RoleModalWithTimer } from '../RoleModalWithTimer'
import { formatFileSize, toErrorText } from '../../../lib/utils'

const initAlternativeData = (data = {}) => ({
  loanAmount: data?.loanAmount || '',
  interestRate: data?.interestRate || '',
  numberOfTranches: data?.numberOfTranches || '',
  downPaymentPercentage: data?.collaterals?.[0]?.downPaymentPercentage,
  comment: '',
  guarantee: false
})

const roleMap = {
  [HEAD_UNDERWRITER]: {
    roleField: 'headUnderwriterResult',
    limit: { sumFrom: 19999999 },
    statusCodeList: [
      'userUnderwriterDecision',
      'userCheckCollateral',
      'userUploadMfoBankDocs',
      'userCheckBankDocs',
      'userHeadUnderwriterDecision'
    ]
  },
  [UNDERWRITER]: {
    roleField: 'underwriterResult',
    limit: { sumTo: 20000000 },
    statusCodeList: ['userUnderwriterDecision', 'userCheckCollateral', 'userUploadMfoBankDocs', 'userCheckBankDocs']
  },

  [SUPER_UNDERWRITER]: {
    roleField: 'superUnderwriterResult',
    statusCodeList: ['userUnderwriterDecision', 'userHeadUnderwriterDecision']
  }
}

const UnderwriterModal = ({ currentModal, closeModal = () => {}, source }) => {
  const navigate = useNavigate()
  const { authStore, authStoreFunc } = useAuthStore()

  const {
    contractStore: { stepCode, loanApplicationData },
    contractStoreFunc: { assignByRole, getApplicationById }
  } = useContractStore()

  const [comment, setComment] = useState('')
  const [reasonforRefusal, setReasonforRefusal] = useState('')
  const [alternativeData, setAlternativeData] = useState([initAlternativeData(loanApplicationData)])
  const [hasLoanApplications, setHasLoanApplications] = useState(null)
  const [openSecondModal, setOpenSecondModal] = useState(false)
  const [filesByCategory, setFilesByCategory] = useState({})
  const [isUploading, setIsUploading] = useState(false)
  const [allFileCategories, setAllFileCategories] = useState(null)
  const [selectedFilesCategory, setSelectedFilesCategory] = useState('')

  const fileInputRefs = useRef({})

  useEffect(() => {
    setAlternativeData(prevData =>
      prevData.length > 0
        ? prevData.map(item => ({
            ...item,
            loanAmount: loanApplicationData.loanAmount,
            interestRate: loanApplicationData.interestRate,
            numberOfTranches: loanApplicationData.numberOfTranches
          }))
        : [initAlternativeData(loanApplicationData)]
    )
  }, [loanApplicationData])

  useEffect(() => {
    const fetchFiles = async () => {
      const response = await CreditFlow.getDocumentTypes()
      setAllFileCategories(response.data.list)
    }

    fetchFiles()
  }, [])

  const handleClose = () => {
    setComment('')
    setAlternativeData([initAlternativeData(loanApplicationData)])
    setOpenSecondModal(false)
    closeModal()
  }

  const handleFileUpload = (event, category) => {
    const uploadedFile = event.target.files?.[0] || event.dataTransfer?.files?.[0]

    if (!uploadedFile) {
      return
    }

    if (uploadedFile) {
      setFilesByCategory(prev => ({ ...prev, [category.code]: uploadedFile }))
    }
  }

  const fileInputClick = categoryId => {
    fileInputRefs.current[categoryId]?.click()
  }

  const getApplicationsList = async data => {
    try {
      const response = await CreditFlow.getApplicationsList(data)
      setHasLoanApplications(response?.data?.list?.length > 0)
    } catch (error) {
      console.error('Error fetching applications:', error)
    }
  }

  const resetState = () => {
    closeModal()
    setFilesByCategory({})
    setIsUploading(false)
  }

  const getUnderwriterComment = () => {
    if (['REFUSE', 'REFUSE_ON_FINANCING'].includes(currentModal)) {
      return [reasonforRefusal?.trim(), comment?.trim() ? `комментарий: ${comment.trim()}` : '']
        .filter(Boolean)
        .join(' ')
    }
    if (['APPROVE', 'REVERT', 'APPROVE_ON_FINANCING', 'REVERT_ON_FINANCING'].includes(currentModal)) {
      return comment?.trim() || ''
    }

    return undefined
  }

  const handleConfirmUnderwriter = debounce(async () => {
    const loanAppId = loanApplicationData?.id

    if (!loanAppId) {
      enqueueSnackbar('Нет данных для обработки!', { variant: 'warning' })
      return
    }

    const role = roleMap[authStore.isUserRole]

    if (!role) {
      enqueueSnackbar('Ваша роль не поддерживается для этой операции', { variant: 'error' })
      return
    }

    const underwriterDecisionData = {
      hasAssign: false,
      statusCodeList: role.statusCodeList || [],
      ...(role?.limit || {})
    }

    const isFinalStage = currentModal && currentModal.endsWith('_ON_FINANCING')
    const roleFieldKey = isFinalStage ? role.roleField.replace('Result', 'FinalResult') : role.roleField

    const statusChangeData = {
      loanApplicationId: loanAppId,
      [roleFieldKey]: currentModal ? currentModal.replace(/_ON_FINANCING$/, '') : '',
      underwriterComment: getUnderwriterComment()
    }

    const modalActionMap = {
      SEND_FOR_REVISION_COLLATERAL_DOCS: { key: 'collateralApprove', value: false },
      CREATE_CONTRACT: { key: 'collateralApprove', value: true },
      SEND_FOR_REVISION_BANK_DOCS: { key: 'bankDocsApprove', value: false },
      SEND_TO_FINAL_CHECK: { key: 'bankDocsApprove', value: true }
    }

    const showSuccess = () => enqueueSnackbar('Успешно', { variant: 'info' })
    const showError = message => enqueueSnackbar(toErrorText(message), { variant: 'error', autoHideDuration: 6000 })

    const sendLoanApplicationResult = async data => {
      try {
        authStoreFunc.setLoading(true)
        const res = await CreditFlow.loanApplicationResult(data)
        if (res?.data) return true
        showError((res && (res.data || res.msg)) || res)
        return false
      } catch (error) {
        console.error('Ошибка отправки данных:', error)
        showError('Ошибка при отправке данных')
        return false
      } finally {
        authStoreFunc.setLoading(false)
      }
    }

    const sendLoanApplicationPromote = async data => {
      try {
        authStoreFunc.setLoading(true)
        const res = await CreditFlow.loanApplicationPromote(data)
        if (res?.data) return true
        showError()
        return false
      } catch (error) {
        console.error('Ошибка отправки данных:', error)
        showError('Ошибка при отправке данных')
        return false
      } finally {
        authStoreFunc.setLoading(false)
      }
    }

    const handleSimpleDecision = async () => {
      const success = await sendLoanApplicationResult(statusChangeData)
      if (success) {
        showSuccess()
        getApplicationsList(underwriterDecisionData)
      }
    }

    const handleAlternativeDecision = async () => {
      const alternativePayload = {
        loanApplicationId: loanAppId,
        alternatives: [...alternativeData]
      }

      try {
        authStoreFunc.setLoading(true)
        const res = await CreditFlow.createAlternatives(alternativePayload)
        if (res?.data) {
          showSuccess()
          await sendLoanApplicationResult(statusChangeData)
          getApplicationsList(underwriterDecisionData)
        } else showError()
      } catch (error) {
        console.error('Ошибка отправки альтернатив:', error)
        showError('Ошибка при отправке альтернатив')
      } finally {
        authStoreFunc.setLoading(false)
      }
    }

    const handleModalAction = async () => {
      const action = modalActionMap[currentModal]
      if (!action) {
        showError(`Неизвестный тип модального действия: ${currentModal}`)
        return
      }

      const actionPayload = {
        loanApplicationId: loanAppId,
        [action.key]: action.value
      }

      if (
        ['SEND_FOR_REVISION_COLLATERAL_DOCS', 'SEND_FOR_REVISION_BANK_DOCS'].includes(currentModal) &&
        comment?.trim()
      ) {
        actionPayload.resultComment = comment.trim()
      }

      const success = await sendLoanApplicationResult(actionPayload)
      if (success) {
        getApplicationById(loanAppId)
        if (authStore.isUserRole === HEAD_UNDERWRITER) {
          handleClose()
        } else {
          setOpenSecondModal(true)
        }
      }
    }

    const handleUploadDocuments = async () => {
      const entries = Object.entries(filesByCategory)

      if (entries.length === 0) {
        enqueueSnackbar('Не выбраны файлы для загрузки!', { variant: 'warning' })

        return
      }

      const uploadPromises = entries.map(async ([categoryCode, file]) => {
        try {
          setIsUploading(true)
          const formData = new FormData()
          formData.append('file', file)
          formData.append('clientId', loanApplicationData?.clientId || '')
          formData.append('loanApplicationId', loanApplicationData?.id || '')
          formData.append('documentTypeCode', categoryCode || '')
          formData.append('uploadStep', getFilteredFileCategories().find(item => item.code === categoryCode).step || '')
          const uploadRes = await CreditFlow.uploadFile(formData)

          if (!uploadRes?.data) {
            throw new Error(`Ошибка загрузки файла для категории ${categoryCode}`)
          }

          return { categoryCode, success: true }
        } catch (error) {
          return { categoryCode, success: false, error }
        }
      })

      const results = await Promise.allSettled(uploadPromises)
      const successCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length
      const failCount = results.filter(r => r.status === 'fulfilled' && !r.value.success).length

      if (successCount > 0) {
        enqueueSnackbar(`Успешно загружено: ${successCount}`, { variant: 'success' })
      }

      if (failCount > 0) {
        enqueueSnackbar(`Не удалось загрузить: ${failCount}`, { variant: 'error' })
      }

      resetState()
      await getApplicationById(loanApplicationData?.id)
      setIsUploading(false)
    }

    const handleSendToPromote = async () => {
      const finalAppData = {
        loanApplicationId: loanAppId,
        message: comment?.trim()
      }

      const success = await sendLoanApplicationPromote(finalAppData)
      if (success) {
        getApplicationById(loanAppId)
        setOpenSecondModal(true)
      }
    }

    switch (currentModal) {
      case 'REVERT':
      case 'APPROVE':
      case 'REFUSE':
      case 'REVERT_ON_FINANCING':
      case 'APPROVE_ON_FINANCING':
      case 'REFUSE_ON_FINANCING':
        await handleSimpleDecision()
        break

      case 'ALTERNATIVE':
        await handleAlternativeDecision()
        break

      case 'SEND_FOR_REVISION_COLLATERAL_DOCS':
      case 'CREATE_CONTRACT':
      case 'SEND_TO_FINAL_CHECK':
      case 'SEND_FOR_REVISION_BANK_DOCS':
        await handleModalAction()
        break

      case 'SUBMIT_FOR_REVIEW': {
        const success = await sendLoanApplicationResult({ loanApplicationId: loanAppId })
        if (success) {
          showSuccess()
          await getApplicationById(loanAppId)
          handleClose()
        }
        break
      }

      case 'SEND_TO_PROMOTE':
        await handleSendToPromote()
        break

      case 'UPLOAD_DOCUMENTS':
        await handleUploadDocuments()
        break

      default:
        handleClose()
        break
    }
  }, 300)

  const handleChangeComment = event => setComment(event.target.value)

  const handleAlternativeChange = (index, event) => {
    const { name, value, type, checked } = event.target

    setAlternativeData(prevData =>
      prevData.map((item, i) => {
        if (i !== index) return item
        const updatedItem = {
          ...item,
          [name]: type === 'checkbox' ? checked : value
        }
        if (name === 'downPaymentPercentage') {
          const parsedPercentage = parseFloat(value || '0')
          const marketValue = loanApplicationData?.collaterals?.[0]?.collateralValue
          if (!isNaN(parsedPercentage) && !isNaN(marketValue)) {
            const downPaymentAmount = (parsedPercentage / 100) * marketValue
            updatedItem.loanAmount =
              marketValue - downPaymentAmount + (loanApplicationData?.collaterals?.[0]?.gpsConnection || 0)
          }
        }
        return updatedItem
      })
    )
  }

  const handleDragOver = event => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleDrop = (event, categoryId) => {
    event.preventDefault()
    event.stopPropagation()
    handleFileUpload(event, categoryId)
  }

  const addAlternative = () => {
    setAlternativeData(prevData => [...prevData, initAlternativeData(loanApplicationData)])
  }

  const deleteAlternative = index => {
    setAlternativeData(prevData => prevData.filter((_, i) => i !== index))
  }

  const nextLoanApplication = async () => {
    await assignByRole()
    await setHasLoanApplications(null)
    await handleClose()
  }

  const stopLoanApplication = async () => {
    await handleClose()
    await navigate('/home')
  }

  const disableConfirm = useMemo(() => {
    if (currentModal === 'REFUSE') {
      return !reasonforRefusal?.trim()
    }

    if (currentModal === 'REVERT') {
      return !comment?.trim()
    }

    if (currentModal === 'ALTERNATIVE') {
      return alternativeData.some(
        data => !data.loanAmount || !data.interestRate || !data.numberOfTranches || !data.downPaymentPercentage
      )
    }

    if (['SEND_FOR_REVISION_COLLATERAL_DOCS', 'SEND_FOR_REVISION_BANK_DOCS'].includes(currentModal)) {
      return !comment?.trim()
    }

    if (currentModal === 'UPLOAD_DOCUMENTS') {
      return !selectedFilesCategory
    }

    return false
  }, [currentModal, comment, reasonforRefusal, alternativeData, selectedFilesCategory])

  const modalConfig = MODAL_CONFIG[currentModal || 'DEFAULT']

  const getFilteredFileCategories = () => {
    if (selectedFilesCategory === 'client-files') {
      return allFileCategories.filter(item => ['userCreateLoanApplication', 'userRevert'].includes(item.step))
    }

    if (selectedFilesCategory === 'pledge-files') {
      return allFileCategories.filter(item => ['userUploadCollateralDocs'].includes(item.step))
    }

    if (['bank-files'].includes(selectedFilesCategory)) {
      return allFileCategories.filter(
        item => ['userUploadClientBankDocs'].includes(item.step) && item.code !== 'PF_ZIP'
      )
    }

    if (selectedFilesCategory === 'wings-files') {
      return allFileCategories.filter(item => ['wings'].includes(item.step))
    }

    if (selectedFilesCategory === 'manual-signing') {
      return allFileCategories.filter(item => ['userManualSigning'].includes(item.step))
    }

    return []
  }

  return (
    <>
      <ActionModal
        open={!!currentModal}
        onClose={handleClose}
        onCancel={handleClose}
        onConfirm={handleConfirmUnderwriter}
        title={modalConfig?.headTitle}
        labelConfirm={modalConfig?.labelConfirm}
        color={modalConfig?.color}
        disableConfirm={disableConfirm}
      >
        <Box display="flex" flexDirection="column" gap="10px">
          <Box>
            <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>{modalConfig?.title}</Typography>
            {modalConfig?.subtitle && <Typography variant="body2">{modalConfig?.subtitle}</Typography>}
          </Box>

          {['SEND_FOR_REVISION_COLLATERAL_DOCS', 'SEND_FOR_REVISION_BANK_DOCS', 'SEND_TO_PROMOTE'].includes(
            currentModal
          ) && (
            <Box>
              <Typography variant="small">Комментарий</Typography>
              <TextField
                name="comment"
                value={comment}
                onChange={handleChangeComment}
                fullWidth
                multiline
                minRows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& .MuiInputBase-input': {
                      padding: 0
                    }
                  }
                }}
              />
            </Box>
          )}

          {['REFUSE', 'REFUSE_ON_FINANCING'].includes(currentModal) && (
            <>
              <Box display={'flex'} flexDirection={'column'}>
                <Typography variant="small">Причина отказа</Typography>
                <SelectPlaceholder
                  IconComponent={props => <ExpandMoreIcon {...props} />}
                  placeholder="Статус"
                  name="statusCode"
                  value={reasonforRefusal || ''}
                  onChange={e => setReasonforRefusal(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="">Не выбрано</MenuItem>
                  <MenuItem value="Более 180 просроченных дней за последние 3 года">
                    Более 180 просроченных дней за последние 3 года
                  </MenuItem>
                  <MenuItem value="Клиент отказывается от финансирования - завершение заявки">
                    Клиент отказывается от финансирования - завершение заявки
                  </MenuItem>
                  <MenuItem value="Лицо пенсионного возраста">Лицо пенсионного возраста</MenuItem>
                  <MenuItem value="Не проходит по AS и BS">Не проходит по AS и BS</MenuItem>
                  <MenuItem value="Недостаточный пакет документов">Недостаточный пакет документов</MenuItem>
                  <MenuItem value="Некорректные данные клиента">Некорректные данные клиента</MenuItem>
                  <MenuItem value="Неплатёжеспособный клиент">Неплатёжеспособный клиент</MenuItem>
                </SelectPlaceholder>
              </Box>

              <Box display={'flex'} flexDirection={'column'}>
                <Typography variant="small">Комментарий</Typography>
                <TextField
                  name="comment"
                  value={comment}
                  onChange={handleChangeComment}
                  fullWidth
                  multiline
                  minRows={3}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& .MuiInputBase-input': {
                        padding: 0
                      }
                    }
                  }}
                />
              </Box>
            </>
          )}

          {['APPROVE', 'REVERT', 'APPROVE_ON_FINANCING', 'REVERT_ON_FINANCING'].includes(currentModal) &&
            !(stepCode === 'userUnderwriterDecision' && currentModal === 'APPROVE') && (
              <Box>
                <Typography variant="small">Комментарий</Typography>
                <TextField
                  name="comment"
                  value={comment}
                  onChange={handleChangeComment}
                  fullWidth
                  multiline
                  minRows={3}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& .MuiInputBase-input': {
                        padding: 0
                      }
                    }
                  }}
                />
              </Box>
            )}

          {currentModal === 'ALTERNATIVE' && (
            <>
              {alternativeData.map((data, index) => (
                <Box display="flex" flexDirection="column" gap="10px">
                  <Box display="flex" justifyContent="space-between" gap="10px" alignItems="center">
                    <Typography
                      sx={{
                        fontSize: '18px',
                        fontWeight: 600
                      }}
                    >
                      {`Предложение ${index + 1}`}
                    </Typography>
                    {index !== 0 && (
                      <IconButton onClick={() => deleteAlternative(index)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                  <Grid2 container spacing={4}>
                    <Grid2 size={{ xs: 6 }}>
                      <Typography variant="small">Сумма займа</Typography>
                      <MaskedTextInput
                        placeholder="0"
                        name="loanAmount"
                        value={data.loanAmount}
                        onChange={event => handleAlternativeChange(index, event)}
                        fullWidth
                        icon="₸"
                        disabled={true}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                      <Typography variant="small">Ставка</Typography>
                      <MaskedTextInput
                        placeholder="0"
                        name="interestRate"
                        value={data.interestRate}
                        onChange={event => handleAlternativeChange(index, event)}
                        fullWidth
                        icon="%"
                        disabled={true}
                        // onInput={e => {
                        //   let value = e.target.value.replace(/[^0-9.,]/g, '')
                        //   value = value.replace(',', '.')
                        //   const numericValue = parseFloat(value)
                        //   if (!isNaN(numericValue) && numericValue > 100) {
                        //     value = '100'
                        //   }
                        //   e.target.value = value
                        // }}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                      <Typography variant="small">Срок</Typography>
                      <MaskedTextInput
                        placeholder="0"
                        name="numberOfTranches"
                        value={data.numberOfTranches}
                        onChange={event => handleAlternativeChange(index, event)}
                        fullWidth
                        mask="999999"
                        icon="Мес"
                        disabled={true}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                      <Typography variant="small">Первоначальный взнос</Typography>
                      <MaskedTextInput
                        placeholder="0"
                        name="downPaymentPercentage"
                        value={data.downPaymentPercentage}
                        onChange={event => handleAlternativeChange(index, event)}
                        fullWidth
                        icon="%"
                        onInput={e => {
                          let value = e.target.value.replace(/[^0-9.,]/g, '')
                          value = value.replace(',', '.')
                          const numericValue = parseFloat(value)
                          if (!isNaN(numericValue) && numericValue > 100) {
                            value = '100'
                          }
                          e.target.value = value
                        }}
                      />
                    </Grid2>
                  </Grid2>
                  <Box>
                    <Typography variant="small">Комментарий</Typography>
                    <TextField
                      name="comment"
                      value={data.comment}
                      onChange={event => handleAlternativeChange(index, event)}
                      fullWidth
                      multiline
                      minRows={3}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& .MuiInputBase-input': {
                            padding: 0
                          }
                        }
                      }}
                    />
                  </Box>
                  <Box>
                    <FormControlLabel
                      name="guarantee"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        padding: 0,
                        margin: 0
                      }}
                      control={
                        <Checkbox
                          color="primary"
                          checked={data.guarantee}
                          onChange={event => handleAlternativeChange(index, event)}
                          sx={{
                            padding: 0
                          }}
                        />
                      }
                      label={<Typography variant="body2">Необходим гарант</Typography>}
                    />
                  </Box>
                </Box>
              ))}
              <Button
                variant="outlined"
                startIcon={<PlusBlueIcon />}
                fullWidth
                onClick={addAlternative}
                sx={{ marginTop: 2 }}
              >
                Добавить предложение
              </Button>
            </>
          )}

          {isUploading ? (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {currentModal === 'UPLOAD_DOCUMENTS' && (
                <Box display="flex" flexDirection="column" gap="15px">
                  <TextField
                    select
                    fullWidth
                    label="Категория"
                    value={selectedFilesCategory}
                    onChange={event => setSelectedFilesCategory(event.target.value)}
                  >
                    {[
                      { label: 'Файлы клиента', code: 'client-files' },
                      { label: 'Файлы залога', code: 'pledge-files' },
                      { label: 'Файлы МФО', code: 'bank-files' },
                      { label: 'Файлы wings', code: 'wings-files' },
                      { label: 'Ручное подписание', code: 'manual-signing' }
                    ].map(item => (
                      <MenuItem key={item.code} value={item.code}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  {getFilteredFileCategories().map(category => {
                    return (
                      <Box
                        key={category?.code}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={2}
                        sx={{
                          border: `1px solid ${GRAY_COLORS[300]}`,
                          borderRadius: '12px',
                          padding: '16px',
                          cursor: 'pointer'
                        }}
                        onClick={() => fileInputClick(category.code)}
                        onDragOver={handleDragOver}
                        onDrop={e => handleDrop(e, category.code)}
                      >
                        <input
                          id={`fileInput-${category.code}`}
                          type="file"
                          ref={el => (fileInputRefs.current[category.code] = el)}
                          hidden
                          onChange={e => handleFileUpload(e, category)}
                        />
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Typography sx={{ fontSize: 14, fontWeight: 600, textAlign: 'center' }} color="info">
                            {category?.name}
                          </Typography>

                          <Typography sx={{ fontSize: 12, fontWeight: 400, textAlign: 'center' }}>
                            Нажмите, чтобы загрузить
                          </Typography>

                          <Typography variant="small">Или перетащите документ в эту область</Typography>
                        </Box>

                        {filesByCategory[category.code] && (
                          <Typography>{filesByCategory[category.code].name}</Typography>
                        )}
                      </Box>
                    )
                  })}
                </Box>
              )}
            </>
          )}
        </Box>
      </ActionModal>

      <ActionModal
        open={hasLoanApplications === true}
        onCancel={stopLoanApplication}
        onConfirm={nextLoanApplication}
        labelConfirm="Продолжить"
        labelCancel="Остановить"
      >
        <RoleModalWithTimer type={currentModal} isQueueEmpty={false} onComplete={nextLoanApplication} />
      </ActionModal>

      <ActionModal open={hasLoanApplications === false}>
        <RoleModalWithTimer type={currentModal} isQueueEmpty={true} />
      </ActionModal>

      <ActionModal open={openSecondModal}>
        <RoleModalWithTimer type={currentModal} isQueueEmpty={true} />
      </ActionModal>
    </>
  )
}

export default UnderwriterModal
